import { isEmpty } from "lodash";
import { useEffect, useRef } from "react";

import { API_DATE_FORMAT } from "src/consts/global";
import { useDateSettings } from "src/contexts/global/ApolloSettingsContext";

/**
 * Refetches entity-tags and removed-tags queries when the point-in-time (PIT) date
 * changes. Uses refs to hold latest variables so the PIT effect only depends on
 * pitDateKey and refetch functions (satisfies react-hooks/exhaustive-deps).
 */
export default function useRefetchTagsOnPitChange({
  entityId,
  entityType,
  currentManageEntityTags,
  tagTypesWithShowingHistory,
  displayTagTypes,
  tagTypesConfigForSelection,
  refetchRemovedTags,
  refetchEntityTags,
}) {
  const dateSettings = useDateSettings();
  const pitDateKey = dateSettings?.toDate?.format?.(API_DATE_FORMAT) ?? "today";
  const hasMountedRef = useRef(false);
  const latestRemovedVarsRef = useRef(null);
  const latestEntityVarsRef = useRef(null);

  useEffect(() => {
    latestRemovedVarsRef.current =
      currentManageEntityTags &&
      entityId &&
      !isEmpty(tagTypesWithShowingHistory)
        ? {
            entityId,
            tagTypes: tagTypesWithShowingHistory,
            tagStatus: "REMOVED",
            entityType,
          }
        : null;
    latestEntityVarsRef.current =
      currentManageEntityTags && entityId && tagTypesConfigForSelection?.length
        ? { entityId, tagTypes: displayTagTypes, entityType }
        : null;
  }, [
    entityId,
    entityType,
    currentManageEntityTags,
    tagTypesWithShowingHistory,
    displayTagTypes,
    tagTypesConfigForSelection,
  ]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    const removedVars = latestRemovedVarsRef.current;
    if (removedVars) {
      void refetchRemovedTags(removedVars);
    }
    const entityVars = latestEntityVarsRef.current;
    if (entityVars) {
      void refetchEntityTags(entityVars);
    }
  }, [pitDateKey, refetchRemovedTags, refetchEntityTags]);
}
